import { Box, Tooltip, Typography } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import LogoIcon from "../../asserts/logo/Icon.svg";
import RightNavigationLogo from "../../asserts/logo/RightNavigationLogo.svg";
import { ReactComponent as GatesIcon } from "../../asserts/logo/gates_icon.svg";
import { ReactComponent as HomeSelectedIcon } from "../../asserts/logo/HomeSelectedIcon.svg";
import { ReactComponent as ProjectsDefaultIcon } from "../../asserts/logo/ProjectsDefaultIcon.svg";
import { ReactComponent as ProjectsSelectedIcon } from "../../asserts/logo/ProjectsSelectedIcon.svg";
import { ReactComponent as MembersSelectedIcon } from "../../asserts/logo/MembersSelectedIcon.svg";
import { ReactComponent as ReportsDefaultIcon } from "../../asserts/logo/ReportsDefaultIcon.svg";
import { ReactComponent as ReportsSelectedIcon } from "../../asserts/logo/ReportsSelectedIcon.svg";
import {
  NAV_RAIL_WIDTH,
  NAV_RAIL_EXPANDED_WIDTH,
  APP_HEADER_HEIGHT,
} from "./layoutConstants";
import { isEnterpriseAiOwner, isPlatformAdmin, isTenantAdmin } from "../../utils/entitlements";

const NAV_ICON_SIZE = 22;
const GATES_ICON_HEIGHT = Math.round((NAV_ICON_SIZE * 15) / 24);
const GATES_ICON_STYLE = {
  width: NAV_ICON_SIZE,
  height: GATES_ICON_HEIGHT,
  display: "block",
};
/** Figma nav icons ship at 22×22 with their own default/selected colours. */
const NAV_SVG_ICON_STYLE = {
  width: NAV_ICON_SIZE,
  height: NAV_ICON_SIZE,
  display: "block",
  // Match MUI's SvgIcon, which opts out of flex shrinking, so selected and
  // default states always render at the same size.
  flexShrink: 0,
};
/**
 * Stroked icons paint half their 1.83px stroke outside the path, so their ink
 * reaches ~20px inside a 22px box. Filled icons have no such overshoot and need
 * a larger box to land on that same 20px.
 */
const NAV_FILLED_ICON_SIZE = 24;
const NAV_FILLED_ICON_STYLE = {
  width: NAV_FILLED_ICON_SIZE,
  height: NAV_FILLED_ICON_SIZE,
  display: "block",
  flexShrink: 0,
};
const PRIMARY = "#005BF0";
const PRIMARY_20 = "#F0F6FF";
const NAV_LABEL_FONT = "Lato, sans-serif";

/** Icons always sit in this fixed column — expand only reveals labels to the right. */
const NAV_ICON_COLUMN_SX = {
  width: NAV_RAIL_WIDTH,
  minWidth: NAV_RAIL_WIDTH,
  maxWidth: NAV_RAIL_WIDTH,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
};

const NAV_LABEL_PANEL_SX = (expanded) => ({
  flex: expanded ? 1 : "0 0 0px",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  opacity: expanded ? 1 : 0,
  visibility: expanded ? "visible" : "hidden",
  transition: "opacity 0.2s ease",
  pr: expanded ? 1.5 : 0,
});

const tooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: "#000000",
      color: "#FFFFFF",
      fontFamily: NAV_LABEL_FONT,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "18px",
      borderRadius: "4px",
      py: "6px",
      px: "10px",
      "& .MuiTooltip-arrow": { color: "#000000" },
    },
  },
};

function NavRailButton({ label, active, expanded, onClick, disabled = false, children }) {
  const button = (
    <Box
      component="button"
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-label={label}
      aria-disabled={disabled ? true : undefined}
      aria-current={active && !disabled ? "page" : undefined}
      sx={{
        border: 0,
        width: "100%",
        height: 48,
        display: "flex",
        alignItems: "stretch",
        bgcolor: active && !disabled ? PRIMARY_20 : "transparent",
        color: disabled ? "#BBBBBB" : active ? PRIMARY : "#555555",
        cursor: disabled ? "not-allowed" : "pointer",
        p: 0,
        m: 0,
        boxSizing: "border-box",
        position: "relative",
        textAlign: "left",
        "&:hover": disabled
          ? { bgcolor: "transparent" }
          : {
              bgcolor: active ? PRIMARY_20 : "#F5F5F5",
            },
        "&:disabled": {
          color: "#BBBBBB",
          bgcolor: "transparent",
          opacity: 1,
          cursor: "not-allowed",
        },
        ...(active &&
          !disabled && {
            "&::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              bgcolor: PRIMARY,
              pointerEvents: "none",
            },
          }),
      }}
    >
      <Box sx={NAV_ICON_COLUMN_SX}>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: NAV_ICON_SIZE,
            height: NAV_ICON_SIZE,
            color: "inherit",
          }}
        >
          {children}
        </Box>
      </Box>
      <Box sx={NAV_LABEL_PANEL_SX(expanded)}>
        <Typography
          component="span"
          sx={{
            fontFamily: NAV_LABEL_FONT,
            fontSize: 14,
            fontWeight: active ? 700 : 400,
            lineHeight: "20px",
            letterSpacing: 0,
            color: "inherit",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );

  if (expanded) return button;

  return (
    <Tooltip title={label} placement="right" arrow slotProps={tooltipSlotProps}>
      <Box
        component="span"
        sx={{
          display: "block",
          width: "100%",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {button}
      </Box>
    </Tooltip>
  );
}

function ProjectsSidebar({ expanded = false, onToggleExpanded }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const roles = user?.roles || [];
  const platformAdmin = isPlatformAdmin(roles);
  const canManageTeam =
    platformAdmin || isTenantAdmin(roles) || isEnterpriseAiOwner(roles);

  const isHomeActive = pathname === "/home-hub";
  const isProjectsActive = pathname === "/projects";
  const isTeamActive = pathname === "/team-members";
  const isReportsActive = pathname === "/reports";
  const isAiUsageActive = pathname === "/ai-usage";

  const railWidth = expanded ? NAV_RAIL_EXPANDED_WIDTH : NAV_RAIL_WIDTH;

  const navItems = [
    {
      key: "home",
      label: "Home",
      active: isHomeActive,
      onClick: () => navigate("/home-hub"),
      icon: isHomeActive ? (
        <HomeSelectedIcon style={NAV_FILLED_ICON_STYLE} />
      ) : (
        <HomeOutlinedIcon sx={{ fontSize: NAV_FILLED_ICON_SIZE }} />
      ),
      show: true,
    },
    {
      key: "projects",
      label: "Projects",
      active: isProjectsActive,
      onClick: () => navigate("/projects"),
      icon: isProjectsActive ? (
        <ProjectsSelectedIcon style={NAV_FILLED_ICON_STYLE} />
      ) : (
        <ProjectsDefaultIcon style={NAV_SVG_ICON_STYLE} />
      ),
      show: true,
    },
    {
      key: "teams",
      label: "Teams",
      active: isTeamActive,
      onClick: () => navigate("/team-members"),
      icon: isTeamActive ? (
        <MembersSelectedIcon style={NAV_SVG_ICON_STYLE} />
      ) : (
        <PeopleOutlinedIcon sx={{ fontSize: NAV_ICON_SIZE }} />
      ),
      show: canManageTeam,
    },
    {
      key: "reports",
      label: "Reports",
      active: isReportsActive,
      onClick: () => navigate("/reports"),
      icon: isReportsActive ? (
        <ReportsSelectedIcon style={NAV_SVG_ICON_STYLE} />
      ) : (
        <ReportsDefaultIcon style={NAV_SVG_ICON_STYLE} />
      ),
      show: true,
    },
    {
      key: "ai-usage",
      label: "AI Usage",
      active: isAiUsageActive,
      onClick: () => navigate("/ai-usage"),
      icon: (
        <InsightsOutlinedIcon
          sx={{ fontSize: NAV_ICON_SIZE, color: isAiUsageActive ? "#005BF0" : undefined }}
        />
      ),
      show: true,
    },
  ];

  return (
    <Box
      sx={{
        width: railWidth,
        borderRight: "1px solid #E6E6E6",
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bgcolor: "#FFFFFF",
        zIndex: 1200,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        transition: "width 0.2s ease",
        overflow: "hidden",
      }}
    >
      {/* Icon stays in same fixed column as Home/Projects; wordmark shows only when expanded */}
      <Box
        sx={{
          width: "100%",
          height: APP_HEADER_HEIGHT,
          display: "flex",
          alignItems: "stretch",
          flexShrink: 0,
          borderBottom: "1px solid #E6E6E6",
        }}
      >
        <Box sx={NAV_ICON_COLUMN_SX}>
          <Box
            component="img"
            src={LogoIcon}
            alt="ValidAIte"
            sx={{ height: 28, width: "auto", display: "block", objectFit: "contain" }}
          />
        </Box>
        <Box sx={NAV_LABEL_PANEL_SX(expanded)}>
          <Box
            component="img"
            src={RightNavigationLogo}
            alt="ValidAIte"
            sx={{ height: 24, width: "auto", display: "block", objectFit: "contain" }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          height: 40,
          display: "flex",
          alignItems: "stretch",
          flexShrink: 0,
          mt: 0.5,
        }}
      >
        <Box sx={NAV_ICON_COLUMN_SX}>
          <Box
            component="button"
            type="button"
            onClick={onToggleExpanded}
            aria-label={expanded ? "Collapse navigation" : "Expand navigation"}
            sx={{
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "4px",
              p: 0,
              m: 0,
              color: "#555555",
              "&:hover": { bgcolor: "#F5F5F5" },
            }}
          >
            {expanded ? (
              <GatesIcon
                style={{
                  ...GATES_ICON_STYLE,
                  transform: "scaleX(-1)",
                }}
              />
            ) : (
              <GatesIcon style={GATES_ICON_STYLE} />
            )}
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={onToggleExpanded}
          sx={{
            ...NAV_LABEL_PANEL_SX(expanded),
            border: 0,
            bgcolor: "transparent",
            cursor: expanded ? "pointer" : "default",
            p: 0,
            m: 0,
            color: "#555555",
            fontFamily: NAV_LABEL_FONT,
            "&:hover": expanded ? { color: "#383838" } : undefined,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: NAV_LABEL_FONT,
              fontSize: 14,
              fontWeight: 400,
              lineHeight: "20px",
              color: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            Collapse
          </Typography>
        </Box>
      </Box>

      <Box sx={{ width: "100%", mt: 0.5, flex: 1 }}>
        {navItems.filter((item) => item.show).map((item) => (
          <NavRailButton
            key={item.key}
            label={item.label}
            active={item.active}
            expanded={expanded}
            onClick={item.onClick}
            disabled={item.disabled}
          >
            {item.icon}
          </NavRailButton>
        ))}
      </Box>
    </Box>
  );
}

export default ProjectsSidebar;
